// Pure per-frame game rules. ctx = { level, practice, sfx, norm }
import { generateLabel, RAINBOW_CHANCE, RAINBOW_SPEED, RAINBOW_BONUS, MISSILE_BATCH, MISSILE_BATCH_PAUSE } from "@/game/gameData";
import { missileLaunch, BOSS_HOVER_Y } from "@/game/logic";

const uid = () => Math.random().toString(36).slice(2);

const effectColor = (target) => {
  if (target.rainbow) return "#ff007f";
  if (target.missile) return "#ff6a00";
  return "#39ff14";
};

const flashMiss = (s, now, sfx) => {
  s.flashUntil = now + 300;
  s.flashColor = "#ff2a2a";
  sfx.miss();
};

const hurt = (s, now, sfx) => {
  s.shakeUntil = now + 350;
  s.flashUntil = now + 320;
  s.flashColor = "#ff2a2a";
  sfx.life();
};

export function spawnTick(s, now, { level, practice }) {
  const canSpawn =
    s.phase === "wave" &&
    (practice || s.spawned < level.quota) &&
    s.aliens.length < level.maxAliens &&
    now - s.lastSpawn > level.spawnInterval;
  if (!canSpawn) return;
  const rainbow = level.id > 1 && Math.random() < RAINBOW_CHANCE;
  s.aliens.push({
    id: uid(),
    label: generateLabel(level, s.aliens.map((a) => a.label)),
    x: 10 + Math.random() * 72,
    y: -0.06,
    rainbow,
    speedMul: rainbow ? RAINBOW_SPEED : 1,
  });
  s.spawned++;
  s.lastSpawn = now;
}

export function moveAliens(s, dt, { level }) {
  for (const a of s.aliens) {
    if (a.missile) {
      a.vx -= a.vx * dt * 1.1;
      a.vy += (level.speed - a.vy) * dt * 1.4;
      a.x = Math.min(94, Math.max(6, a.x + a.vx * dt));
      a.y += a.vy * dt;
    } else {
      a.y += level.speed * a.speedMul * dt;
    }
  }
}

// returns true when the player ran out of lives
export function escapeTick(s, now, { practice, sfx }) {
  const gone = s.aliens.filter((a) => a.y >= 1);
  if (!gone.length) return false;
  s.aliens = s.aliens.filter((a) => a.y < 1);
  const escaped = gone.filter((a) => !a.rainbow).length;
  if (!escaped) return false;
  s.escaped += escaped;
  if (practice) {
    s.flashUntil = now + 200;
    s.flashColor = "#ffb000";
    sfx.miss();
    return false;
  }
  s.lives -= escaped;
  hurt(s, now, sfx);
  return s.lives <= 0;
}

export function maybeStartBoss(s, { level, practice }) {
  if (practice || s.phase !== "wave" || s.spawned < level.quota || s.aliens.length) return;
  s.phase = "boss";
  s.boss = { tokens: level.bossTokens, hit: 0, x: 50, y: -0.06, flash: 0, stage: "enter", fired: 0, lastFire: 0 };
}

export function missileTick(s, now, { level, sfx }) {
  const b = s.boss;
  const kind = level.boss;
  const batchBreak = b.fired > 0 && b.fired % MISSILE_BATCH === 0;
  const interval = batchBreak ? level.missileBatchPause ?? MISSILE_BATCH_PAUSE : Math.max(900, level.spawnInterval * 0.55);
  if (b.fired < kind.missiles && now - b.lastFire > interval) {
    s.aliens.push({
      id: uid(),
      label: generateLabel(level, s.aliens.map((a) => a.label)),
      ...missileLaunch(kind, b, level),
      missile: true,
      speedMul: 1,
    });
    b.fired++;
    b.lastFire = now;
    sfx.missile();
  }
  if (b.fired >= kind.missiles && s.aliens.length === 0) b.stage = "attack";
}

// returns true when the boss reached the bottom (instant game over)
export function bossTick(s, dt, now, ctx) {
  if (s.phase !== "boss" || !s.boss) return false;
  const { level, sfx } = ctx;
  const b = s.boss;
  if (b.flash > 0) b.flash -= dt * 3;
  if (level.boss.pattern === "spiral" && b.stage !== "enter") b.x = 50 + Math.sin(now / 1100) * 24;
  if (b.stage === "enter") {
    b.y += level.bossSpeed * 3 * dt;
    if (b.y >= BOSS_HOVER_Y) {
      b.y = BOSS_HOVER_Y;
      b.stage = "missiles";
      b.lastFire = now + 600;
    }
    return false;
  }
  if (b.stage === "missiles") {
    missileTick(s, now, ctx);
    return false;
  }
  b.y += level.bossSpeed * dt;
  if (b.y < 1) return false;
  s.lives = 0;
  hurt(s, now, sfx);
  return true;
}

// one frame; returns "over" when the game ends
export function step(s, dt, now, ctx) {
  spawnTick(s, now, ctx);
  moveAliens(s, dt, ctx);
  if (escapeTick(s, now, ctx)) return "over";
  maybeStartBoss(s, ctx);
  if (bossTick(s, dt, now, ctx)) return "over";
  s.effects = s.effects.filter((e) => now - e.at < 500);
  return null;
}

export function beginWave(s, { level }) {
  const now = performance.now();
  s.phase = "wave";
  s.startTime = now;
  s.lastSpawn = now - level.spawnInterval;
}

function fireAtAliens(s, guess, now, { level, norm, sfx }) {
  let target = null;
  for (const a of s.aliens) {
    if (norm(a.label) === guess && (!target || a.y > target.y)) target = a;
  }
  if (!target) return flashMiss(s, now, sfx);
  s.aliens = s.aliens.filter((a) => a.id !== target.id);
  s.destroyed++;
  const base = 100 + level.id * 20 + Math.round(target.y * 50);
  s.score += target.rainbow ? base * RAINBOW_BONUS : base;
  s.hitChars += target.label.length;
  s.effects.push({
    id: Math.random(),
    x: target.x,
    y: target.y,
    at: now,
    color: effectColor(target),
    text: target.rainbow ? `+${base * RAINBOW_BONUS}` : null,
  });
  if (target.rainbow) sfx.rainbow();
  else sfx.explode();
}

// returns "complete" when the boss is defeated
function fireAtBoss(s, guess, now, { level, norm, sfx }) {
  const token = norm(s.boss.tokens[s.boss.hit]);
  if (guess !== token) return flashMiss(s, now, sfx);
  s.boss.hit++;
  s.hitChars += token.length;
  s.boss.flash = 1;
  s.score += 80;
  s.effects.push({ id: Math.random(), x: s.boss.x, y: s.boss.y, at: now, color: level.boss.color });
  sfx.bossHit();
  if (s.boss.hit < s.boss.tokens.length) return null;
  s.score += 500;
  return "complete";
}

// resolves the typed line; returns "complete" when the level is won
export function fire(s, ctx) {
  const guess = ctx.norm(s.typed.trim());
  s.typed = "";
  if (!guess) return null;
  const now = performance.now();
  const attacking = s.phase === "boss" && s.boss?.stage === "attack";
  if (attacking) return fireAtBoss(s, guess, now, ctx);
  if (s.phase === "wave" || s.phase === "boss") fireAtAliens(s, guess, now, ctx);
  return null;
}

export function summarize(s, type, level) {
  const elapsedMin = (performance.now() - s.startTime) / 60000;
  const wpm = elapsedMin > 0 ? Math.round(s.hitChars / 5 / elapsedMin) : 0;
  const accuracy = s.typedChars > 0 ? Math.round((s.hitChars / s.typedChars) * 100) : 100;
  return {
    type,
    score: s.score,
    wpm,
    accuracy: Math.min(100, accuracy),
    level: level.id,
    destroyed: s.destroyed,
    escaped: s.escaped,
  };
}
